import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAccessibility } from "../../context/AccessibilityContext";

const MENU = [
  { label: "Account Settings", path: "/account/settings", icon: "⚙" },
  { label: "My Reports", path: "/account/reports", icon: "📄" },
  { label: "Accessibility", path: "/account/accessibility", icon: "♿" },
  { label: "Plans & Subscriptions", path: "/account/plans", icon: "💳" },
];

function Sidebar({ active }: { active: any }) {
  return (
    <div style={{ width: "220px", flexShrink: 0, background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "16px 0", height: "fit-content" }}>
      {MENU.map(item => (
        <Link key={item.path} to={item.path}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 20px", fontSize: "14px", color: active === item.path ? "#C9A84C" : "var(--text-primary)", fontWeight: active === item.path ? 700 : 400, background: active === item.path ? "rgba(201,168,76,0.08)" : "transparent", borderLeft: active === item.path ? "3px solid #C9A84C" : "3px solid transparent", textDecoration: "none" }}>
          <span>{item.icon}</span> {item.label}
        </Link>
      ))}
      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }}></div>
      <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/"; }}
        style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 20px", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500, borderLeft: "3px solid transparent" }}>
        <span>🚪</span> Logout
      </button>
    </div>
  );
}

function Toggle({ value, onChange, label, desc }: { value: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "3px", maxWidth: "380px" }}>{desc}</div>
      </div>
      <button onClick={() => onChange(!value)}
        style={{ width: "48px", height: "26px", borderRadius: "13px", background: value ? "#C9A84C" : "var(--border)", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: value ? "25px" : "3px", transition: "left 0.2s" }}></div>
      </button>
    </div>
  );
}

function RadioGroup({ value, onChange, options, label, desc }: { value: string; onChange: (v: string) => void; options: {value: string; label: string; preview?: string}[]; label: string; desc: string }) {
  return (
    <div style={{ padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px" }}>{desc}</div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {options.map(opt => (
          <button key={opt.value} onClick={() => onChange(opt.value)}
            style={{ padding: "8px 16px", borderRadius: "8px", border: value === opt.value ? "2px solid #C9A84C" : "1.5px solid var(--border)", background: value === opt.value ? "rgba(201,168,76,0.1)" : "var(--card-bg)", color: value === opt.value ? "#C9A84C" : "var(--text-primary)", fontSize: "13px", fontWeight: value === opt.value ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
            {opt.preview && <span style={{ marginRight: "6px" }}>{opt.preview}</span>}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Accessibility() {
  const { settings, saveSettings } = useAccessibility();
  const [local, setLocal] = useState(settings);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const location = useLocation();

  useEffect(() => { setLocal(settings); }, [settings]);

  const update = (key: string, value: any) => {
    setLocal(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await saveSettings(local);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = async () => {
    const defaults = {
      font_size: "medium", high_contrast: false, colour_blind_mode: "none",
      reduce_motion: false, read_aloud: false, show_hovercards: true, underline_links: false
    };
    setLocal(defaults);
    await saveSettings(defaults);
  };

  const sectionTitle = (title: string, subtitle: string) => (
    <div style={{ marginBottom: "4px", paddingTop: "8px" }}>
      <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{title}</div>
      <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{subtitle}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "24px", color: "var(--text-primary)" }}>Account</h1>
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        <Sidebar active={location.pathname} />
        <div style={{ flex: 1 }}>

          <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "28px 32px", marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "20px", borderBottom: "1px solid var(--border)", marginBottom: "8px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>Accessibility</h2>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "3px" }}>Customise your experience for better readability and usability</p>
              </div>
              <button onClick={handleReset}
                style={{ fontSize: "12px", color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border)", padding: "6px 12px", borderRadius: "6px", cursor: "pointer" }}>
                Reset to defaults
              </button>
            </div>

            {sectionTitle("Vision", "Adjust colours and contrast for better visibility")}

            <RadioGroup value={local.font_size} onChange={v => update("font_size", v)}
              label="Font Size"
              desc="Controls the size of text across the entire website"
              options={[
                { value: "small", label: "Small", preview: "A" },
                { value: "medium", label: "Medium", preview: "A" },
                { value: "large", label: "Large", preview: "A" },
                { value: "xlarge", label: "Extra Large", preview: "A" },
              ]} />

            <Toggle value={local.high_contrast} onChange={v => update("high_contrast", v)}
              label="High Contrast Mode"
              desc="Increases colour contrast between text and background for better readability" />

            <RadioGroup value={local.colour_blind_mode} onChange={v => update("colour_blind_mode", v)}
              label="Colour Blind Mode"
              desc="Applies a colour filter optimised for different types of colour blindness"
              options={[
                { value: "none", label: "None" },
                { value: "deuteranopia", label: "Deuteranopia", preview: "🟢" },
                { value: "protanopia", label: "Protanopia", preview: "🔴" },
                { value: "tritanopia", label: "Tritanopia", preview: "🔵" },
              ]} />

            <Toggle value={local.underline_links} onChange={v => update("underline_links", v)}
              label="Underline All Links"
              desc="Adds underline to all clickable links to make them easier to identify" />

            <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>Motion and Animation</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Control page animations and transitions</div>
            </div>

            <Toggle value={local.reduce_motion} onChange={v => update("reduce_motion", v)}
              label="Reduce Motion"
              desc="Minimises animations and transitions across the website for users sensitive to motion" />

            <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>Interaction</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>Control how you interact with the website</div>
            </div>

            <Toggle value={local.read_aloud} onChange={v => update("read_aloud", v)}
              label="Read Aloud"
              desc="Enables text-to-speech. Select any text on the page and it will be read aloud using your browser's built-in speech engine" />

            <Toggle value={local.show_hovercards} onChange={v => update("show_hovercards", v)}
              label="Show Hovercards"
              desc="Shows additional information popup cards when you hover over tools and links" />

            <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
              <button onClick={handleSave} disabled={saving}
                style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "11px 28px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : "Save Settings"}
              </button>
              {saved && <span style={{ color: "var(--success)", fontSize: "13px", fontWeight: 600 }}>Settings saved and applied!</span>}
            </div>
          </div>

          <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "24px 32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "6px" }}>Sign Language Video Guides</h3>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", lineHeight: "1.6", maxWidth: "380px" }}>
                  Step-by-step video guides for each tool explained in Indian Sign Language (ISL). Coming soon — our team is currently recording these guides.
                </p>
              </div>
              <span style={{ background: "#EAE6E0", color: "#5C5C5C", fontSize: "11px", fontWeight: 700, padding: "4px 10px", borderRadius: "8px", whiteSpace: "nowrap" }}>Coming Soon</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
